import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import * as Mui from "@mui/material";
import {
  Close,
  CloseFullscreen,
} from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { parentBoxStyle } from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import dayjs from "dayjs";
import { useFetch } from "services/hooks/useFetch";
import {
  APP_BAR_OFFSET,
  designColors,
  designRadii,
  designShell,
  designType,
  FORM_ACTION_BUTTON_WIDTH,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalFieldLabel,
  useExpandedContentLeft,
} from "components/DesignSystem/sharedUiPatterns";
import { SelectedEmployeeHeader } from "../SelectedEmployeeHeader";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

const toDayjsDate = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;
  if (/^\d{2}-\d{2}-\d{4}$/.test(String(value))) {
    const [day, month, year] = String(value).split("-");
    return dayjs(`${year}-${month}-${day}`);
  }
  return dayjs(value);
};

const toDayjsTime = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;
  const text = String(value);
  return dayjs(`2000-01-01T${text.length === 5 ? `${text}:00` : text}`);
};

export const Apply = ({
  onClose,
  setReCallApi,
  open = true,
  expanded = false,
  onToggleExpand,
  editRecord = null,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      date: null,
      startTime: null,
      endTime: null,
      reason: "",
      button_loader: false,
    },
  });
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const { domain, userDetails, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const { result: users } = useFetch(
    `${domain}team_zone/approve/work_adjustment/users-by-type/?user_id=${userDetails.id}&type=${encodeURIComponent("On Duty")}&company_id=${topbarCompanyId || ""}`,
  );
  const contentLeft = useExpandedContentLeft(expanded);
  // See Leave/Apply.js for the full explanation — this used to always post
  // status:"Approved" regardless of the creator's actual approver
  // permission, so any editor auto-approved their own on duty creation.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "On Duty",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "On Duty",
    );

  useEffect(() => {
    if (open) {
      if (editRecord) {
        reset({
          user_name: editRecord?.employee_name || "",
          user_id: editRecord?.user_id || "",
          selectedUser: {
            ...editRecord,
            label: editRecord?.employee_name,
            user_id: editRecord?.user_id,
            image: editRecord?.image_url,
          },
          date: toDayjsDate(editRecord?.date),
          startTime: toDayjsTime(editRecord?.start_time),
          endTime: toDayjsTime(editRecord?.end_time),
          reason: editRecord?.reason || "",
          button_loader: false,
        });
      } else {
        reset();
      }
      setShowAutoCompleteAvathar(false);
    }
  }, [editRecord, open, reset]);

  const updateUserName = (event, data) => {
    if (data) {
      setValue("user_name", data?.label);
      setValue("user_id", data?.user_id);
      setValue("selectedUser", data);
      setShowAutoCompleteAvathar(false);
    } else {
      setValue("user_name", null);
      setValue("user_id", "");
      setValue("selectedUser", null);
      setShowAutoCompleteAvathar(true);
    }
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const payload = {
      ...(editRecord?.id && { id: editRecord.id }),
      user_id: data.user_id,
      attendance_type: "On Duty",
      reason: data.reason,
      ...(!editRecord && { status: hasApproverAccess ? "Approved" : "Pending" }),
      date: data.date?.format("YYYY-MM-DD"),
      start_time: dayjs(data.startTime).format("HH:mm"),
      end_time: dayjs(data.endTime).format("HH:mm"),
    };
    const response = await customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${topbarCompanyId}`,
      editRecord ? "PUT" : "POST",
      payload,
    );
    if (response?.ok) {
      showToast(
        "success",
        editRecord ? "On duty request updated." : "On duty request created.",
      );
      setReCallApi(true);
      reset();
      onClose();
      setValue("button_loader", false);
    } else {
      setValue("button_loader", false);
      showToast(
        "error",
        response?.message ||
          `Couldn't ${editRecord ? "update" : "create"} the on duty request. Please try again.`,
      );
    }
  };

  const panelContent = (
    <Box
      sx={{
        ...parentBoxStyle,
        height: "100%",
        borderRadius: expanded ? designRadii.card : 0,
        overflow: "hidden",
        backgroundColor: designColors.white,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          backgroundColor: designColors.white,
        }}
      >
        <Box sx={{ p: 3, pb: 0 }}>
          <Mui.Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              pb: 2.25,
              borderBottom: `1px solid ${designColors.slate200}`,
            }}
          >
            <Box>
              <Typography sx={designType.panelTitle}>
                {editRecord ? "Edit On Duty Request" : "New On Duty Request"}
              </Typography>
              <Typography sx={{ ...designType.pageSubtitle, mt: 0.5 }}>
                {editRecord
                  ? "Update the on duty request details."
                  : "Create an on duty request for a selected date"}
              </Typography>
            </Box>
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              {onToggleExpand ? (
                expanded ? (
                  <PanelHeaderIconButton
                    expanded
                    title="Collapse"
                    onClick={onToggleExpand}
                  >
                    <CloseFullscreen />
                  </PanelHeaderIconButton>
                ) : (
                  <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                    <IconButton
                      onClick={onToggleExpand}
                      size="small"
                      sx={{
                        ...designShell.panelSoftIconButton,
                        color: designColors.slate500,
                      }}
                    >
                      <OpenFullViewIcon fontSize="small" />
                    </IconButton>
                  </Mui.Tooltip>
                )
              ) : null}
              {expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Close"
                  onClick={onClose}
                >
                  <Close />
                </PanelHeaderIconButton>
              ) : (
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    ...designShell.panelSoftIconButton,
                    color: designColors.slate500,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Mui.Stack>
          </Mui.Stack>
        </Box>
      </Paper>
      <Mui.Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Box>
              <GlobalFieldLabel helperText="Select the employee for whom the On Duty request is being created.">
                Employee name
              </GlobalFieldLabel>
              <CustomAutoCompleteWithLogo
                dataList={users?.map((obj) => ({
                  ...obj,
                  label: obj.employee_name,
                  image: obj?.image_url,
                }))}
                selectedValue={watch("selectedUser")}
                rules={{
                  ...register("user_name", {
                    required: "User name is required",
                  }),
                }}
                updateDataFunction={updateUserName}
                placeHolder="Select the name"
                errorMessage={
                  errors?.user_name?.message ? errors?.user_name?.message : ""
                }
                textBoxError={errors.user_name ? true : false}
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Box>
            <SelectedEmployeeHeader selectedUser={watch("selectedUser")} />
            <Box>
              <GlobalFieldLabel helperText="Select the attendance date to which the On Duty request applies.">
                Date
              </GlobalFieldLabel>
              <CustomizedDatePicker
                name="date"
                control={control}
                required
                requiredMessage="Date is required"
                placeholder="Select date"
                disableFuture
                handleDisabledFutureDate={true}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Select the start time for the On Duty request.">
                Start Time
              </GlobalFieldLabel>
              <CustomTimePicker
                name="startTime"
                control={control}
                errors={errors}
                validateFn={(value) => {
                  if (!value || !dayjs(value).isValid()) {
                    return "Start time is required";
                  }
                  return true;
                }}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Select the end time for the On Duty request.">
                End Time
              </GlobalFieldLabel>
              <CustomTimePicker
                name="endTime"
                control={control}
                errors={errors}
                validateFn={(value) => {
                  if (!value || !dayjs(value).isValid()) {
                    return "End time is required";
                  }
                  const startTime = watch("startTime");
                  if (
                    startTime &&
                    dayjs(startTime).isValid() &&
                    !dayjs(value).isAfter(dayjs(startTime))
                  ) {
                    return "End time must be after start time";
                  }
                  return true;
                }}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Enter the reason for the On Duty request.">
                Reason
              </GlobalFieldLabel>
              <CustomTextBox
                type="multiline"
                rules={{
                  ...register("reason", {
                    required: "Reason is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "At least one alphabet is required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason")}
                updateDataFunction={(event) => {
                  if (event.target.value.length <= 1024) {
                    const nextValue = event.target.value;
                    setValue("reason", nextValue, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    if (atleastOneCharacterWithSpace.test(nextValue)) {
                      clearErrors("reason");
                    }
                  }
                }}
                placeHolder="Enter the reason for on duty"
                errorMessage={
                  errors?.reason?.message ? errors?.reason?.message : ""
                }
                textBoxError={errors?.reason ? true : false}
              />
            </Box>
          </Box>
        </Box>
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: `1px solid ${designColors.slate200}`,
            backgroundColor: designColors.white,
          }}
        >
          <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton
              type="submit"
              onClick={handleSubmit(onSubmit)}
              loading={Boolean(watch("button_loader"))}
              disabled={Boolean(watch("button_loader"))}
              sx={{ minWidth: FORM_ACTION_BUTTON_WIDTH }}
            >
              {editRecord ? "Update" : hasApproverAccess ? "Approve" : "Apply"}
            </PrimaryButton>
          </Mui.Stack>
        </Mui.Box>
      </Mui.Box>
    </Box>
  );

  if (expanded) {
    return createPortal(
      <Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Box>
      </Box>,
      document.body,
    );
  }

  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={panelDrawerSx()}
    >
      <Box sx={{ width: PANEL_DRAWER_WIDTH, maxWidth: "100%", height: "100%" }}>
        {panelContent}
      </Box>
    </Mui.Drawer>
  );
};
